import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Tasks, Users } from "@/api/resources";
import type { TaskOut } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { useToast } from "@/components/ui/Toast";

export function TaskAssignment({ task, myUserId, closed }: { task: TaskOut; myUserId?: string; closed: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [choosing, setChoosing] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [failure, setFailure] = useState<string | null>(null);
  const people = useQuery({ queryKey: ["users"], queryFn: Users.list, enabled: choosing });
  const mine = Boolean(myUserId) && task.assignee_id === myUserId;
  const invalidate = () => {
    for (const key of [["task", task.id], ["tasks"], ["project-tasks"], ["activity"]]) {
      void qc.invalidateQueries({ queryKey: key });
    }
  };
  const change = useMutation({
    mutationFn: (target: "claim" | string | null) => target === "claim"
      ? Tasks.claim(task.id) : Tasks.assign(task.id, target, task.version),
    onSuccess: (updated) => {
      qc.setQueryData(["task", task.id], updated);
      setChoosing(false);
      setFailure(null);
      setRecipient("");
      toast.success(updated.assignee_id ? `Исполнитель: ${updated.assignee_name ?? "участник"}` : "Задача свободна");
      invalidate();
    },
    onError: (error: Error) => {
      const message = "current" in error
        ? "Задача изменилась. Проверь обновлённую карточку и повтори выбор."
        : error.message || "Не удалось изменить исполнителя. Попробуй ещё раз.";
      setFailure(message);
      invalidate();
    },
  });
  return (
    <Card className="space-y-3">
      <p className="text-meta text-hint">Исполнитель</p>
      <p className="text-cell font-medium" aria-live="polite">
        {task.assignee_id ? `${task.assignee_name ?? "Участник"}${mine ? " · ты" : ""}` : "Задача свободна"}
      </p>
      {task.assignee_id && !mine && <p className="text-meta">Задача уже занята. Договориться о передаче можно в обсуждении ниже.</p>}
      {failure && <p role="alert" className="text-meta text-dstr">{failure}</p>}
      {!closed && myUserId && <div className="flex flex-wrap gap-2">
        {!task.assignee_id && <Button size="compact" loading={change.isPending} onClick={() => change.mutate("claim")}>Взять себе</Button>}
        {mine && <>
          <Button size="compact" variant="secondary" disabled={change.isPending} onClick={() => setChoosing(!choosing)}>Передать товарищу</Button>
          <Button size="compact" variant="ghost" disabled={change.isPending} onClick={() => change.mutate(null)}>Освободить</Button>
        </>}
      </div>}
      {choosing && mine && !closed && <div className="space-y-2">
        {people.isError ? <Button size="compact" variant="ghost" onClick={() => void people.refetch()}>Повторить загрузку участников</Button> : <>
          <label className="block text-meta">Кому передать
            <select aria-label="Кому передать задачу" className="mt-1 min-h-touch w-full rounded-card border border-sep bg-bg px-3 text-text" value={recipient} disabled={people.isLoading || change.isPending} onChange={(e) => setRecipient(e.target.value)}>
              <option value="">Выбери участника</option>
              {(people.data ?? []).filter((u) => u.id !== myUserId).map((u) => <option key={u.id} value={u.id}>{u.display_name}</option>)}
            </select>
          </label>
          {people.data && people.data.filter((u) => u.id !== myUserId).length === 0 && <p className="text-meta text-hint">Других активных участников пока нет.</p>}
          <Button size="compact" disabled={!recipient} loading={change.isPending} onClick={() => change.mutate(recipient)}>Передать</Button>
        </>}
      </div>}
    </Card>
  );
}
